import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Keyboard, X, Command } from 'lucide-react';

/**
 * ShortcutsHUD — global `?` keyboard overlay listing all known shortcuts.
 * Toggle: press `?` anywhere (outside inputs) or call the exposed event.
 */

const SHORTCUTS = [
  { group: 'Navigation', items: [
    { keys: ['⌘/Ctrl', 'K'], desc: 'Globale Suche & Schnellzugriff' },
    { keys: ['⌘/Ctrl', '/'], desc: 'Suche öffnen' },
    { keys: ['G', 'D'], desc: 'Zu Dashboard' },
    { keys: ['G', 'C'], desc: 'Zu Unternehmen' },
    { keys: ['G', 'A'], desc: 'Zu Analytics' },
  ]},
  { group: 'Aktionen', items: [
    { keys: ['N'], desc: 'Neues Zertifikat/Eintrag' },
    { keys: ['S'], desc: 'Speichern (in Formularen)' },
    { keys: ['Esc'], desc: 'Modal / Panel schließen' },
  ]},
  { group: 'UI', items: [
    { keys: ['?'], desc: 'Diese Übersicht anzeigen' },
    { keys: ['T'], desc: 'Theme wechseln' },
    { keys: ['M'], desc: 'Benachrichtigungen stumm' },
  ]},
];

function Key({ children }) {
  return (
    <kbd className="inline-flex items-center justify-center min-w-[28px] h-7 px-1.5 rounded-md border border-[#E4E4E7] bg-white text-[11px] font-bold text-primary shadow-[inset_0_-1px_0_rgba(0,0,0,0.04)]">
      {children}
    </kbd>
  );
}

export default function ShortcutsHUD() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      // Ignore when typing in inputs/textareas.
      const tag = (e.target?.tagName || '').toLowerCase();
      if (['input', 'textarea', 'select'].includes(tag) || e.target?.isContentEditable) return;
      if (e.key === '?' && (e.shiftKey || e.key === '?')) {
        e.preventDefault();
        setOpen(v => !v);
      } else if (e.key === 'Escape') {
        setOpen(false);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[250] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setOpen(false)}
          data-testid="shortcuts-hud"
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0, y: 12 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.97, opacity: 0 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-[24px] max-w-2xl w-full max-h-[85vh] overflow-y-auto shadow-[0_32px_80px_rgba(17,24,39,0.2)]"
          >
            <div className="sticky top-0 bg-white/90 backdrop-blur-md px-5 py-4 border-b border-[#F4F4F5] flex items-center justify-between z-10">
              <div className="flex items-center gap-2">
                <Keyboard className="w-5 h-5 text-[#0F8A5F]" />
                <h2 className="font-heading font-bold text-primary">Tastatur-Shortcuts</h2>
              </div>
              <button onClick={() => setOpen(false)} className="p-1.5 hover:bg-[#F4F4F5] rounded-lg">
                <X className="w-4 h-4 text-muted-app" />
              </button>
            </div>

            <div className="p-5 space-y-6">
              {SHORTCUTS.map(g => (
                <div key={g.group}>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-secondary font-bold mb-3">{g.group}</p>
                  <ul className="space-y-1.5">
                    {g.items.map((s, i) => (
                      <li key={i} className="flex items-center justify-between gap-4 py-2 px-3 rounded-lg hover:bg-[#F9FAFB]">
                        <span className="text-[13.5px] text-primary">{s.desc}</span>
                        <span className="flex items-center gap-1 flex-shrink-0">
                          {s.keys.map((k, j) => (
                            <React.Fragment key={j}>
                              {j > 0 && <span className="text-muted-app text-[11px]">+</span>}
                              <Key>{k}</Key>
                            </React.Fragment>
                          ))}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
              <div className="p-3 rounded-xl bg-[#F9FAFB] border border-[#E4E4E7] text-[12px] text-secondary flex items-start gap-2">
                <Command className="w-4 h-4 text-[#0F8A5F] flex-shrink-0 mt-0.5" />
                <span>Drücke <Key>?</Key> jederzeit, um diese Übersicht aufzurufen. Die Shortcuts sind in Listen und Dashboards aktiv — nicht während du tippst.</span>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
